import React, { useState, useRef, useEffect } from 'react';

/**
 * Dropdown multiselect for alert categories (checkbox list).
 * Empty selection = all categories (no server filter).
 */
function CategoryMultiSelect({
  options = [],
  selected = [],
  onChange,
  title = 'Filter by category (server-side). Select one or more.',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selectableOptions = options.filter(
    (opt) => opt?.value && opt.value !== 'all' && opt.label,
  );

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const toggleCategory = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const getTriggerLabel = () => {
    if (selected.length === 0) return 'All Categories';
    if (selected.length === 1) {
      const match = selectableOptions.find((o) => o.value === selected[0]);
      return match?.label || selected[0];
    }
    return `${selected.length} categories`;
  };

  return (
    <div className="alerts-filter-dropdown category-multiselect" ref={rootRef}>
      <button
        type="button"
        className={`alerts-filter-dropdown-trigger category-multiselect-trigger ${open ? 'open' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={title}
      >
        <span className="alerts-filter-dropdown-label category-multiselect-label">{getTriggerLabel()}</span>
        <span className="alerts-filter-dropdown-chevron category-multiselect-chevron" aria-hidden="true">
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open && (
        <div className="alerts-filter-dropdown-menu category-multiselect-menu" role="listbox" aria-multiselectable="true">
          <div className="category-multiselect-menu-header">
            <span>Select categories</span>
            {selected.length > 0 && (
              <button
                type="button"
                className="category-multiselect-clear"
                onClick={clearAll}
              >
                Clear
              </button>
            )}
          </div>
          {selectableOptions.map((cat) => {
            const isChecked = selected.includes(cat.value);
            return (
              <div
                key={cat.value}
                role="option"
                aria-selected={isChecked}
                className={`category-multiselect-option ${isChecked ? 'checked' : ''}`}
                onClick={() => toggleCategory(cat.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCategory(cat.value);
                  }
                }}
                tabIndex={0}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                />
                <span className="category-multiselect-option-label">{cat.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CategoryMultiSelect;
